import { ReactNode, useState, useRef, useEffect } from 'react'
import { FiArrowUp, FiArrowDown, FiInfo } from 'react-icons/fi'

function colorWithAlpha(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '')
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

interface MetricCardProps {
  title: string
  value: string
  trend: string
  icon: ReactNode
  iconBgColor: string
  iconBgColorCustom?: string
  style?: React.CSSProperties
  // Graph data - optional, no graph shown when empty/absent
  graphData?: number[]
  // Graph color - optional, defaults to green
  graphColor?: string
  // KPI description - shown as tooltip on info icon
  description?: string
  loading?: boolean
  onClick?: () => void
}

const MetricCard = ({ 
  title, 
  value, 
  trend, 
  icon, 
  iconBgColor, 
  iconBgColorCustom, 
  style,
  graphData: providedGraphData,
  graphColor = '#13AB5B',
  description,
  loading = false,
  onClick,
}: MetricCardProps) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const infoRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
        infoRef.current && !infoRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false)
      }
    }
    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showTooltip])

  // Parse trend: "↑12.3% MoM" or "↓5.1% MoM"
  const isNeutralTrend = !trend || trend === '—' || trend.trim() === '—'
  const trendMatch = trend.match(/(↑|↓)\s*(\d+\.?\d*%?)/)
  const trendDirection = trendMatch?.[1] ?? '↑'
  const percentage = trendMatch ? (trendMatch[2].includes('%') ? trendMatch[2] : `${trendMatch[2]}%`) : ''
  const trendText = trend.replace(/(↑|↓)\s*\d+\.?\d*%?\s*/, '').trim()
  const trendIsUp = trendDirection === '↑'
  const trendColor = isNeutralTrend ? '#69697A' : trendIsUp ? '#13AB5B' : '#E93323'

  // Graph data - only show graph when real data is provided
  const graphData =
    providedGraphData && providedGraphData.length > 0
      ? providedGraphData.length === 1
        ? [providedGraphData[0], providedGraphData[0]]
        : providedGraphData
      : null
  const minValue = graphData ? Math.min(...graphData) : 0
  const maxValue = graphData ? Math.max(...graphData) : 0
  const valueRange = graphData ? Math.max(maxValue - minValue, 1) : 1
  const gradientId = `grad-${title.toLowerCase().replace(/\s+/g, '')}`
  const graphHeight = 43
  const graphWidth = 90.02531433105469
  const graphPaddingX = 4

  return (
    <div 
      className={`bg-white relative transition-shadow${onClick ? ' cursor-pointer hover:shadow-sm' : ''}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      style={{
        width: '100%',
        height: '108px',
        border: '1px solid #E9E9EA',
        borderRadius: '10px',
        padding: '16px',
        boxSizing: 'border-box',
        opacity: 1,
        ...style
      }}
    >
      <div className="flex items-start justify-between" style={{ height: '100%' }}>
        {/* Left Side - Icon, Title, Value */}
        <div className="flex flex-col" style={{ height: '100%', justifyContent: 'space-between' }}>
          {/* Icon at top */}
          <div 
            className={`${iconBgColorCustom ? '' : iconBgColor} flex items-center justify-center`}
            style={{ 
              width: '28px',  
              height: '28px',
              minWidth: '28px',
              minHeight: '28px',
              maxWidth: '28px',
              maxHeight: '28px',
              backgroundColor: iconBgColorCustom || undefined,
              borderRadius: '4px',
              boxSizing: 'border-box',
              padding: 0,
              margin: 0,
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {icon}
          </div>
          
          {/* Title below icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px', position: 'relative' }}>
            <p 
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0%',
                color: '#69697A',
                margin: 0,
                width: 'auto',
                height: '16px',
                opacity: 1,
                whiteSpace: 'nowrap',
                display: 'block'
              }}
            >
              {title}
            </p>
            {description && (
              <>
                <button
                  ref={infoRef}
                  onClick={(event) => {
                    event.stopPropagation()
                    setShowTooltip(!showTooltip)
                  }}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  aria-label={`Info about ${title}`}
                >
                  <FiInfo style={{ width: '12px', height: '12px', color: '#9CA3AF' }} />
                </button>
                {showTooltip && (
                  <div
                    ref={tooltipRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '6px',
                      backgroundColor: '#212126',
                      color: '#FFFFFF',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      lineHeight: '16px',
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      width: '240px',
                      zIndex: 50,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    {description}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Value below title */}
          {loading ? (
            <div
              className="animate-pulse"
              style={{ width: 140, height: 24, backgroundColor: '#E9E9EA', borderRadius: 6 }}
            />
          ) : (
            <p 
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
                margin: 0,
                width: 'auto',
                minHeight: '28px',
                opacity: 1,
                whiteSpace: 'nowrap'
              }}
            >
              {value}
            </p>
          )}
        </div>

        {/* Right Side - Graph, Percentage, Trend Text */}
        <div className="flex flex-col items-end" style={{ height: '100%', justifyContent: 'space-between' }}>
          {/* Graph at top - only render when real data provided */}
          {graphData ? (
          <div 
            className="relative overflow-hidden" 
            style={{ 
              width: `${graphWidth * 0.75}px`, 
              height: `${graphHeight * 0.75}px`,
              background: 'white',
              flexShrink: 0
            }}
          >
            <svg
              width={graphWidth * 0.75}
              height={graphHeight * 0.75}
              viewBox={`0 0 ${graphWidth} ${graphHeight}`}
              preserveAspectRatio="none"
              style={{ display: 'block', background: 'white' }}
            >
              <defs>
                <linearGradient
                  id={gradientId}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2={graphHeight}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor={colorWithAlpha(graphColor, 0.22)} />
                  <stop offset="1" stopColor={colorWithAlpha(graphColor, 0)} />
                </linearGradient>
              </defs>
              <rect width={graphWidth} height={graphHeight} fill="white" />
              {(() => {
                const points = graphData.map((val, i) => {
                  const drawableWidth = Math.max(graphWidth - graphPaddingX * 2, 1)
                  const x = graphPaddingX + (i / (graphData.length - 1)) * drawableWidth
                  const y = graphHeight - ((val - minValue) / valueRange) * (graphHeight - 4) - 2
                  return { x, y }
                })
                let linePath = `M ${points[0].x} ${points[0].y} `
                for (let i = 0; i < points.length - 1; i++) {
                  const current = points[i]
                  const next = points[i + 1]
                  const cp1x = current.x + (next.x - current.x) / 3
                  const cp1y = current.y
                  const cp2x = current.x + 2 * (next.x - current.x) / 3
                  const cp2y = next.y
                  linePath += `C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y} `
                }
                const areaPath = `${linePath}L ${graphWidth - graphPaddingX} ${graphHeight} L ${graphPaddingX} ${graphHeight} Z`
                return (
                  <>
                    <path d={areaPath} fill={`url(#${gradientId})`} />
                    <path
                      d={linePath.trim()}
                      stroke={graphColor}
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </>
                )
              })()}
            </svg>
          </div>
          ) : null}

          {/* Percentage / neutral dash */}
          {isNeutralTrend ? (
            <p
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#69697A',
                margin: 0,
                marginTop: '3px',
              }}
            >
              —
            </p>
          ) : trend && percentage ? (
            <div className="flex items-center" style={{ gap: '3px', marginTop: '3px' }}>
              {trendIsUp ? (
                <FiArrowUp style={{ width: '12px', height: '12px', color: trendColor, flexShrink: 0 }} />
              ) : (
                <FiArrowDown style={{ width: '12px', height: '12px', color: trendColor, flexShrink: 0 }} />
              )}
              <span style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0%',
                color: trendColor,
              }}>
                {percentage}
              </span>
            </div>
          ) : null}

          {/* Trend text below percentage - only show if trend exists */}
          {!isNeutralTrend && trend && trendText && (
            <p style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0%',
              color: '#69697A',
              margin: 0,
              marginTop: '2px',
              textAlign: 'right'
            }}>
              {trendText}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default MetricCard

